/**
 * Reward Customer Manager
 * 客户数据管理器：统一管理客户积分余额和推荐链接
 */
(function () {
    'use strict';

    /**
     * 客户数据管理器（单例模式）
     */
    class RewardCustomerManager {
        constructor() {
            // 客户数据缓存
            this.customerData = null;

            // 数据加载状态
            this.isLoading = false;
            this.loadPromise = null;

            // 事件名称常量
            this.EVENTS = {
                CUSTOMER_DATA_LOADED: 'reward:customer:loaded',
                POINTS_BALANCE_UPDATED: 'reward:customer:points:updated',
                REFERRAL_LINK_UPDATED: 'reward:customer:referral:updated'
            };

            // 自动初始化
            this.init();
        }

        /**
         * 初始化
         */
        init() {
            // 监听积分消耗事件
            if (window.RewardDataManager) {
                window.addEventListener(window.RewardDataManager.EVENTS.POINTS_UPDATED, () => {
                    this.refreshCustomerData();
                });
            }
        }

        /**
         * 获取客户数据
         * @param {boolean} forceRefresh - 强制刷新
         * @returns {Promise<object>}
         */
        async getCustomerData(forceRefresh = false) {
            // 如果有缓存且不强制刷新，直接返回
            if (this.customerData && !forceRefresh) {
                return this.customerData;
            }

            // 如果正在加载，等待加载完成
            if (this.isLoading && this.loadPromise) {
                return await this.loadPromise;
            }

            // 开始加载
            this.isLoading = true;
            this.loadPromise = this.fetchCustomerData();

            try {
                const data = await this.loadPromise;
                this.customerData = data;

                // 触发数据加载完成事件
                this.dispatchEvent(this.EVENTS.CUSTOMER_DATA_LOADED, {
                    data: data
                });

                return data;
            } finally {
                this.isLoading = false;
                this.loadPromise = null;
            }
        }

        /**
         * 从API获取客户数据
         */
        async fetchCustomerData() {
            try {
                if (!window.RewardApiService) {
                    throw new Error('RewardApiService is not loaded');
                }

                // 检查用户是否登录
                const email = this.getCustomerEmail();
                if (!email) {
                    throw new Error('Customer is not logged in');
                }

                // 调用 /growave-api/customer 接口
                const response = await window.RewardApiService.get('/growave-api/customer', {
                    email: email,
                    locale: this.getLocale()
                });

                // 解析数据
                const customerData = {
                    pointsBalance: this.extractPointsBalance(response),
                    referralLink: this.extractReferralLink(response),
                    rawData: response
                };

                return customerData;
            } catch (error) {
                console.error('Failed to fetch customer data:', error);
                throw error;
            }
        }

        /**
         * 提取积分余额
         */
        extractPointsBalance(response) {
            // 尝试多种可能的数据结构
            if (response.points_module && typeof response.points_module.pointsBalance !== 'undefined') {
                return response.points_module.pointsBalance;
            }
            if (response.pointsBalance !== undefined) {
                return response.pointsBalance;
            }
            if (response.data && response.data.points_module && typeof response.data.points_module.pointsBalance !== 'undefined') {
                return response.data.points_module.pointsBalance;
            }
            if (response.data && response.data.pointsBalance !== undefined) {
                return response.data.pointsBalance;
            }
            return 0;
        }

        /**
         * 提取推荐链接
         */
        extractReferralLink(response) {
            // 尝试多种可能的数据结构
            if (response.referralLink) {
                return response.referralLink;
            }
            if (response.data && response.data.referralLink) {
                return response.data.referralLink;
            }
            return '';
        }

        /**
         * 获取客户邮箱
         */
        getCustomerEmail() {
            // 从Shopify全局对象获取
            if (window.Shopify && window.Shopify.customerEmail) {
                return window.Shopify.customerEmail;
            }

            // 从meta标签获取
            const metaEmail = document.querySelector('meta[name="customer-email"]');
            if (metaEmail && metaEmail.content) {
                return metaEmail.content;
            }

            // 未登录时返回 null
            return null;
        }

        /**
         * 获取语言环境
         */
        getLocale() {
            // 从Shopify全局对象获取
            if (window.Shopify && window.Shopify.locale) {
                return window.Shopify.locale;
            }

            // 从HTML lang属性获取
            const htmlLang = document.documentElement.lang;
            if (htmlLang) {
                return htmlLang;
            }

            return 'en';
        }

        /**
         * 刷新客户数据
         */
        async refreshCustomerData() {
            const data = await this.getCustomerData(true);

            // 触发积分余额更新事件
            this.dispatchEvent(this.EVENTS.POINTS_BALANCE_UPDATED, {
                pointsBalance: data.pointsBalance
            });

            // 触发推荐链接更新事件
            this.dispatchEvent(this.EVENTS.REFERRAL_LINK_UPDATED, {
                referralLink: data.referralLink
            });

            return data;
        }

        /**
         * 获取积分余额
         */
        async getPointsBalance(forceRefresh = false) {
            const data = await this.getCustomerData(forceRefresh);
            return data.pointsBalance;
        }

        /**
         * 获取推荐链接
         */
        async getReferralLink(forceRefresh = false) {
            const data = await this.getCustomerData(forceRefresh);
            return data.referralLink;
        }

        /**
         * 触发自定义事件
         */
        dispatchEvent(eventName, detail = {}) {
            const event = new CustomEvent(eventName, {
                detail: detail,
                bubbles: true,
                cancelable: true
            });
            window.dispatchEvent(event);
        }

        /**
         * 监听积分余额更新
         */
        onPointsBalanceUpdate(callback) {
            window.addEventListener(this.EVENTS.POINTS_BALANCE_UPDATED, (event) => {
                callback(event.detail.pointsBalance, event);
            });
        }

        /**
         * 监听推荐链接更新
         */
        onReferralLinkUpdate(callback) {
            window.addEventListener(this.EVENTS.REFERRAL_LINK_UPDATED, (event) => {
                callback(event.detail.referralLink, event);
            });
        }

        /**
         * 获取客户的活跃折扣奖励
         * @param {boolean} forceRefresh - 强制刷新
         * @returns {Promise<Array>}
         */
        async getActiveRewards(forceRefresh = false) {
            try {
                if (!window.RewardApiService) {
                    throw new Error('RewardApiService is not loaded');
                }

                // 检查用户是否登录
                const email = this.getCustomerEmail();
                if (!email) {
                    throw new Error('Customer is not logged in');
                }

                // 调用 /growave-api/customer-active-rewards 接口
                const response = await window.RewardApiService.get('/growave-api/customer-active-rewards', {
                    email: email,
                    locale: this.getLocale()
                });

                // 提取 activeRewards 数据
                let activeRewards = [];
                if (response && response.data && response.data.activeRewards) {
                    activeRewards = response.data.activeRewards;
                } else if (response && response.activeRewards) {
                    activeRewards = response.activeRewards;
                }

                return activeRewards || [];
            } catch (error) {
                console.error('Failed to fetch active rewards:', error);
                return [];
            }
        }

        /**
         * 获取客户的活动历史记录
         * @param {boolean} forceRefresh - 强制刷新
         * @returns {Promise<Array>}
         */
        async getActivityHistory(forceRefresh = false) {
            try {
                if (!window.RewardApiService) {
                    throw new Error('RewardApiService is not loaded');
                }

                // 检查用户是否登录
                const email = this.getCustomerEmail();
                if (!email) {
                    throw new Error('Customer is not logged in');
                }

                // 调用 /growave-api/activity-history 接口
                const response = await window.RewardApiService.get('/growave-api/activity-history', {
                    email: email,
                    locale: this.getLocale(),
                    page: 1,
                    pageSize: 999
                });

                // 提取 activityHistories 数据
                let activityHistories = [];
                if (response && response.data && response.data.activityHistories) {
                    activityHistories = response.data.activityHistories;
                } else if (response && response.activityHistories) {
                    activityHistories = response.activityHistories;
                }

                return activityHistories || [];
            } catch (error) {
                console.error('Failed to fetch activity history:', error);
                return [];
            }
        }

        /**
         * 获取积分规则（已登录用户）
         * @returns {Promise<Array>}
         */
        async getEarningRules() {
            try {
                if (!window.RewardApiService) {
                    throw new Error('RewardApiService is not loaded');
                }

                // 检查用户是否登录
                const email = this.getCustomerEmail();
                if (!email) {
                    throw new Error('Customer is not logged in');
                }

                // 调用 /growave-api/earning-rules 接口
                const response = await window.RewardApiService.get('/growave-api/earning-rules', {
                    email: email,
                    locale: this.getLocale()
                });

                // 提取 earningRuleResults 数据
                let earningRuleResults = [];
                if (response && response.data && response.data.earningRuleResults) {
                    earningRuleResults = response.data.earningRuleResults;
                } else if (response && response.earningRuleResults) {
                    earningRuleResults = response.earningRuleResults;
                }

                return earningRuleResults || [];
            } catch (error) {
                console.error('Failed to fetch earning rules:', error);
                return [];
            }
        }

        /**
         * 获取积分规则（未登录用户）
         * @returns {Promise<Array>}
         */
        async getEarningRulesUnlogin() {
            try {
                if (!window.RewardApiService) {
                    throw new Error('RewardApiService is not loaded');
                }

                // 调用 /growave-api/unlogin/earning-rules 接口（不传参数）
                const response = await window.RewardApiService.get('/growave-api/unlogin/earning-rules');

                // 提取 earningRuleResults 数据
                let earningRuleResults = [];
                if (response && response.data && response.data.earningRuleResults) {
                    earningRuleResults = response.data.earningRuleResults;
                } else if (response && response.earningRuleResults) {
                    earningRuleResults = response.earningRuleResults;
                }

                return earningRuleResults || [];
            } catch (error) {
                console.error('Failed to fetch earning rules (unlogin):', error);
                return [];
            }
        }

        /**
         * 获取消费积分规则（已登录用户）
         * @returns {Promise<Array>}
         */
        async getSpendingRules() {
            try {
                if (!window.RewardApiService) {
                    throw new Error('RewardApiService is not loaded');
                }

                // 检查用户是否登录
                const email = this.getCustomerEmail();
                if (!email) {
                    throw new Error('Customer is not logged in');
                }

                // 调用 /growave-api/spending-rules 接口
                const response = await window.RewardApiService.get('/growave-api/spending-rules', {
                    email: email,
                    locale: this.getLocale()
                });

                // 提取 spendingRuleResults 数据
                let spendingRuleResults = [];
                if (response && response.data && response.data.spendingRuleResults) {
                    spendingRuleResults = response.data.spendingRuleResults;
                } else if (response && response.spendingRuleResults) {
                    spendingRuleResults = response.spendingRuleResults;
                }

                return spendingRuleResults || [];
            } catch (error) {
                console.error('Failed to fetch spending rules:', error);
                return [];
            }
        }

        /**
         * 获取消费积分规则（未登录用户）
         * @returns {Promise<Array>}
         */
        async getSpendingRulesUnlogin() {
            try {
                if (!window.RewardApiService) {
                    throw new Error('RewardApiService is not loaded');
                }

                // 调用 /growave-api/unlogin/spending-rules 接口（不传参数）
                const response = await window.RewardApiService.get('/growave-api/unlogin/spending-rules');

                // 提取 spendingRuleResults 数据
                let spendingRuleResults = [];
                if (response && response.data && response.data.spendingRuleResults) {
                    spendingRuleResults = response.data.spendingRuleResults;
                } else if (response && response.spendingRuleResults) {
                    spendingRuleResults = response.spendingRuleResults;
                }

                return spendingRuleResults || [];
            } catch (error) {
                console.error('Failed to fetch spending rules (unlogin):', error);
                return [];
            }
        }

        /**
         * 更新客户生日
         * @param {number} year - 年份
         * @param {number} month - 月份 (1-12)
         * @param {number} day - 日期 (1-31)
         * @returns {Promise<object>}
         */
        async updateBirthday(year, month, day) {
            try {
                if (!window.RewardApiService) {
                    throw new Error('RewardApiService is not loaded');
                }

                // 检查用户是否登录
                const email = this.getCustomerEmail();
                if (!email) {
                    throw new Error('Customer is not logged in');
                }

                // 调用 /growave-api/update-birthday 接口
                const response = await window.RewardApiService.post('/growave-api/update-birthday', {
                    country: this.getCountry(),
                    customerIdentifier: email,
                    day: day,
                    locale: this.getLocale(),
                    month: month,
                    year: year
                });

                console.log('Birthday updated successfully:', response);
                return response;
            } catch (error) {
                console.error('Failed to update birthday:', error);
                throw error;
            }
        }

        /**
         * 发送奖励事件
         * @param {string} ruleId - 规则ID
         * @param {string} ruleType - 规则类型
         * @returns {Promise<object>}
         */
        async sendEvent(ruleId, ruleType) {
            try {
                if (!window.RewardApiService) {
                    throw new Error('RewardApiService is not loaded');
                }

                // 检查用户是否登录
                const email = this.getCustomerEmail();
                if (!email) {
                    throw new Error('Customer is not logged in');
                }

                // 调用 /growave-api/rewards/send-event 接口
                const response = await window.RewardApiService.post('/growave-api/rewards/send-event', {
                    country: this.getCountry(),
                    customerIdentifier: email,
                    locale: this.getLocale(),
                    ruleId: ruleId,
                    ruleType: ruleType
                });

                console.log('Event sent successfully:', response);

                // 延迟触发赚取积分成功事件和刷新数据,避免接口限流和后端数据延迟
                setTimeout(() => {
                    // 触发赚取积分成功事件,通知其他组件刷新数据(包括activity history和earning rules)
                    const earnSuccessEvent = new CustomEvent('reward:earn:success', {
                        detail: {
                            ruleId: ruleId,
                            ruleType: ruleType,
                            response: response
                        },
                        bubbles: true,
                        cancelable: true
                    });
                    window.dispatchEvent(earnSuccessEvent);

                    // 触发积分获取页面刷新事件,更新DOM中的卡券状态
                    const earnPointRefreshEvent = new CustomEvent('reward:earnPoint:refresh', {
                        detail: {
                            ruleId: ruleId,
                            ruleType: ruleType
                        },
                        bubbles: true,
                        cancelable: true
                    });
                    window.dispatchEvent(earnPointRefreshEvent);

                    // 刷新积分余额
                    this.refreshCustomerData().catch(err => {
                        console.error('Failed to refresh customer data after sendEvent:', err);
                    });

                    // 刷新积分规则（某些规则完成后需要更新状态）
                    this.getEarningRules().catch(err => {
                        console.error('Failed to refresh earning rules after sendEvent:', err);
                    });
                }, 1000);

                return response;
            } catch (error) {
                console.error('Failed to send event:', error);
                throw error;
            }
        }

        /**
         * 兑换积分
         * @param {number} pointsToSpend - 消耗的积分
         * @param {string} ruleId - 规则ID
         * @param {number} selectedVariantId - 选择的变体ID (默认为0)
         * @returns {Promise<object>}
         */
        async redeemPoints(pointsToSpend, ruleId, selectedVariantId = 1) {
            try {
                if (!window.RewardApiService) {
                    throw new Error('RewardApiService is not loaded');
                }

                // 检查用户是否登录
                const email = this.getCustomerEmail();
                if (!email) {
                    throw new Error('Customer is not logged in');
                }

                // 调用 /growave-api/rewards/redeem 接口
                const response = await window.RewardApiService.post('/growave-api/rewards/redeem', {
                    country: this.getCountry(),
                    customerIdentifier: email,
                    locale: this.getLocale(),
                    pointsToSpend: pointsToSpend,
                    ruleId: ruleId,
                    selectedVariantId: 1
                });

                console.log('Points redeemed successfully:', response);

                return response;
            } catch (error) {
                console.error('Failed to redeem points:', error);
                throw error;
            }
        }

        /**
         * 获取客户折扣码
         * @param {number} pointsToSpend - 消耗的积分
         * @param {string} ruleId - 规则ID
         * @param {number} selectedVariantId - 选择的变体ID (默认为1)
         * @returns {Promise<object>}
         */
        async getCustomerDiscountCode(pointsToSpend, ruleId, selectedVariantId = 1) {
            try {
                if (!window.RewardApiService) {
                    throw new Error('RewardApiService is not loaded');
                }

                // 检查用户是否登录
                const email = this.getCustomerEmail();
                if (!email) {
                    throw new Error('Customer is not logged in');
                }

                // 调用 /growave-api/customer-discountcode 接口
                const response = await window.RewardApiService.post('/growave-api/customer-discountcode', {
                    country: this.getCountry(),
                    customerIdentifier: email,
                    locale: this.getLocale(),
                    pointsToSpend: pointsToSpend,
                    ruleId: ruleId,
                    selectedVariantId: 1
                });

                console.log('Customer discount code fetched:', response);

                return response;
            } catch (error) {
                console.error('Failed to get customer discount code:', error);
                throw error;
            }
        }

        /**
         * 获取国家代码
         */
        getCountry() {
            // 从Shopify全局对象获取
            if (window.Shopify && window.Shopify.country) {
                return window.Shopify.country;
            }

            // 从meta标签获取
            const metaCountry = document.querySelector('meta[name="customer-country"]');
            if (metaCountry) {
                return metaCountry.content;
            }

            // 默认值
            return 'US';
        }
    }

    // 导出单例实例
    window.RewardCustomerManager = new RewardCustomerManager();
})();
